<template>
  <div class="induction-heating-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$P_heat ∝ f² · B_max²$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          
          <!-- Base Coil Unit -->
          <g transform="translate(100, 180)">
             <!-- Coil Turns -->
             <g :stroke="coilColor" stroke-width="4" fill="none">
               <path v-for="i in 8" :key="'c'+i" :d="`M ${i*25},-5 Q ${i*25 + 12.5},-20 ${i*25 + 25},-5`" />
               <path v-for="i in 8" :key="'cb'+i" :d="`M ${i*25},-5 Q ${i*25 + 12.5},10 ${i*25 + 25},-5`" stroke-width="2" opacity="0.4" />
             </g>
             
             <!-- Animated Magnetic Field Lines (Changing B field) -->
             <g v-if="localPower > 0" opacity="0.6">
               <path v-for="i in 4" :key="'m'+i" :d="`M ${40 + i*40},-10 C ${20 + i*40},-80 ${60 + i*40},-80 ${40 + i*40},-10`" 
                     fill="none" stroke="#22d3ee" stroke-width="1.5" stroke-dasharray="8,4">
                  <!-- Flowing animation simulating alternating flux -->
                  <animate attributeName="stroke-dashoffset" from="12" to="0" :dur="`${0.5 / (powerFreq/20)}s`" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.2;0.8;0.2" :dur="`${1 / (powerFreq/20)}s`" repeatCount="indefinite" />
               </path>
             </g>
          </g>

          <!-- Glass Ceramic Cooktop Surface (Non-conductive) -->
           <rect x="50" y="165" width="300" height="6" fill="rgba(148, 163, 184, 0.2)" stroke="#475569" stroke-width="1"/>
           <text x="355" y="172" fill="#64748b" font-size="9">微晶玻璃面板</text>

          <!-- The Pot Bottom (Ferromagnetic Conductor) -->
          <g transform="translate(100, 15)">
             <!-- Pan shape -->
             <path d="M -10,150 L 0,30 L 220,30 L 230,150 Z" :fill="potBodyColor" stroke="#94a3b8" stroke-width="2" />
             
             <!-- The hot plate bottom showing heat -->
             <rect x="-10" y="145" width="240" height="5" :fill="bottomHeatColor" />

             <!-- Induced Eddy Currents (涡电流漩涡) -->
             <g v-if="localPower > 0">
                <!-- Circular spirals viewed from side/angle -->
                <ellipse v-for="i in 6" :key="'e'+i" :cx="i*35 + 10" cy="147" rx="15" ry="3" fill="none" :stroke="heatColor" stroke-width="1.5">
                   <animate attributeName="rx" values="2;15;2" :dur="`${1.5 / (powerFreq/20)}s`" repeatCount="indefinite" />
                   <animate attributeName="opacity" values="1;0;1" :dur="`${1.5 / (powerFreq/20)}s`" repeatCount="indefinite" />
                </ellipse>
             </g>

             <!-- Boiling Water bubbles -->
             <g v-if="currentTemp > 60">
               <circle v-for="b in bbls" :key="'b'+b.id" :cx="b.x" :cy="b.y" :r="b.r" fill="rgba(255,255,255,0.6)" />
             </g>
             
             <!-- Steam -->
             <g v-if="currentTemp > 90" fill="rgba(255,255,255,0.2)">
               <path d="M 50,30 Q 70,0 90,30 Q 70,10 50,30">
                  <animate attributeName="transform" type="translate" values="0,0; -10,-40" dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.8;0" dur="2s" repeatCount="indefinite" />
               </path>
               <path d="M 120,30 Q 140,0 160,30 Q 140,10 120,30">
                  <animate attributeName="transform" type="translate" values="0,0; 10,-50" dur="2.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.8;0" dur="2.5s" repeatCount="indefinite" />
               </path>
             </g>
          </g>

        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>设定交流高频档位</label>
              <span class="val">{{ powerFreq.toFixed(0) }} <small>kHz</small></span>
           </div>
           <div class="hud-item orange">
              <label>铁锅实时温度</label>
              <span class="val" :style="{ color: currentTemp > 80 ? '#ef4444' : '#f8fafc' }">{{ currentTemp.toFixed(1) }} <small>°C</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">调节电磁炉功率/频率 (影响交变磁场剧烈程度)</label>
                    <el-slider v-model="localPower" :min="0" :max="100" :step="1" size="small"></el-slider>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>电磁炉完全没有发热电阻丝！底部的励磁线圈通入高频交流电（20-40 kHz）产生极速变化的磁场。磁感线穿过上方的微晶玻璃面板（绝缘体，无感应），砸在铁磁性锅具底部。依据<strong>法拉第电磁感应定律</strong>，变化的磁通量在整块锅底金属内部激发出闭合的感应电流圈——<strong>涡流 (Eddy Current)</strong>。由于锅底自身有电阻（焦耳热 <code>Q = I²Rt</code>），无数疯狂打转的涡流瞬间将其加热至沸腾。调大功率本质是增强磁场频率和幅度，使生热激增！</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InductionHeatingViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      localPower: 0,
      currentTemp: 25, // Room temp
      timer: null,
      bbls: []
    }
  },
  computed: {
    powerFreq() {
       return 20 + (this.localPower / 100) * 20; // 20kHz to 40kHz typical
    },
    coilColor() {
       if (this.localPower === 0) return '#475569';
       // Pulse color based on power
       let intensity = 150 + this.localPower;
       return `rgb(${intensity}, ${intensity/2}, 50)`;
    },
    heatColor() {
       if (this.localPower === 0) return 'rgba(255,255,255,0)';
       let r = 200 + this.localPower * 0.5;
       let g = 200 - this.localPower * 1.5;
       return `rgba(${r}, ${g}, 40, 0.8)`;
    },
    bottomHeatColor() {
       if (this.currentTemp < 30) return '#334155';
       let tRatio = (this.currentTemp - 25) / 100; // max around 120
       if (tRatio > 1) tRatio = 1;
       let r = 50 + tRatio * 200;
       let g = 65 - tRatio * 20;
       let b = 85 - tRatio * 60;
       return `rgb(${r}, ${g}, ${b})`;
    },
    potBodyColor() {
       let tRatio = (this.currentTemp - 25) / 100;
       if (tRatio > 1) tRatio = 1;
       let r = 30 + tRatio * 40;
       let g = 40;
       let b = 50;
       return `rgb(${r}, ${g}, ${b})`;
    }
  },
  methods: {
    animate() {
       // Thermal simulation
       // Heating power is proportional to localPower^2 (simplified)
       let heatingRate = Math.pow(this.localPower / 100, 2) * 2.0; 
       
       // Cooling rate proportional to temp diff to room
       let coolingRate = (this.currentTemp - 25) * 0.05;
       
       this.currentTemp += (heatingRate - coolingRate) * 0.1;
       
       if (this.currentTemp < 25) this.currentTemp = 25;
       // Temp cap
       if (this.currentTemp > 120) this.currentTemp = 120;

       this.updateBubbles();

       this.timer = requestAnimationFrame(this.animate);
    },
    updateBubbles() {
       // Generate bubbles if boiling (temp > 95)
       if (this.currentTemp > 95 && Math.random() < 0.3) {
          this.bbls.push({
             id: Math.random(),
             x: 20 + Math.random() * 180,
             y: 140,
             r: 2 + Math.random() * 3,
             v: 1 + Math.random() * 2
          });
       }
       
       for(let i = this.bbls.length - 1; i >= 0; i--) {
          let b = this.bbls[i];
          b.y -= b.v;
          if (b.y < 40) { // reaches surface
             this.bbls.splice(i, 1);
          }
       }
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    if(this.timer) cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.induction-heating-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
